import React, { MouseEvent, MutableRefObject } from 'react';
import { FaExpand } from 'react-icons/fa';
import {
    PiArrowClockwise,
    PiArrowCounterClockwise,
    PiPause,
    PiPlay,
    PiRectangle,
    PiSpeakerHigh,
    PiSpeakerSimpleX,
    PiSubtitles,
} from 'react-icons/pi';
import ProgressSlider from './ProgressSlider';
import { Slider, Tooltip } from '@nextui-org/react';
import styles from './Controls.module.css';
import { formatTime } from '../TimeFormat';
import { Chapter } from '../App';

const video_speeds = {
    maxSpeed: 2,
    minSpeed: 0.25,
    step: 0.25,
};


interface ControlsProps {
    onPlayPause: () => void;
    playing: boolean;
    onRewind: () => void;
    onForward: () => void;
    played: number;
    playedSeconds: number;
    onSeek: (value: number | number[]) => void;
    onSeekMouseUp: (value: number | number[]) => void;
    onVolumeChangeHandler: (value: number | number[]) => void;
    onVolumeSeekUp: (value: number | number[]) => void;
    volume: number;
    muted: boolean;
    onMute: () => void;
    duration: number;
    currentTime: number;
    onMouseSeekDown: () => void;
    controlRef: MutableRefObject<HTMLDivElement | null>;
    playBackRate: number;
    setPlayBackRate: (value: number) => void;
    showSubtitles: boolean;
    toggleSubtitles: () => void;
    chapters: Chapter[];
    onToggleFullscreen: () => void;
}

const Controls = (props: ControlsProps) => {
    const speedSliderRef = React.useRef<HTMLInputElement | null>(null);
    const volumeSliderRef = React.useRef<HTMLInputElement | null>(null);

    const togglePlaybackRate = () => {
        if (props.playBackRate === video_speeds.maxSpeed) {
            props.setPlayBackRate(video_speeds.minSpeed);
        } else {
            props.setPlayBackRate(props.playBackRate + video_speeds.step);
        }
    };

    return (
        <div
            className={`flex flex-col justify-between z-10 absolute inset-0 w-full items-center transition-all duration-500 ease-in-out ${styles.controlsContainer}`}
            ref={props.controlRef}>
            <div></div>
            <div className='flex flex-row w-full items-center h-16 px-8 pb-8 opacity-80'>
                <button onClick={props.onRewind} className="cursor-pointer">
                    <PiArrowCounterClockwise className={styles.icon} />
                </button>
                <button onClick={props.onPlayPause} className="cursor-pointer">
                    {props.playing ? <PiPause className={styles.icon} /> : <PiPlay className={styles.icon} />}
                </button>
                <button onClick={props.onForward} className="cursor-pointer">
                    <PiArrowClockwise className={styles.icon} />
                </button>
                <Tooltip
                    closeDelay={1000}
                    classNames={{ content: 'p-1' }}
                    content={
                        <div
                            className='w-4'
                            onClick={(e: MouseEvent<HTMLDivElement>) => {
                                if (!volumeSliderRef.current) return;
                                let rect = volumeSliderRef.current.getBoundingClientRect();
                                const clickPercentage = (-(e.clientY - rect.bottom) / rect.height) * 100;
                                props.onVolumeChangeHandler(clickPercentage);
                            }}
                        >
                            <Slider
                                ref={volumeSliderRef}
                                size='sm'
                                className='h-32 w-4'
                                minValue={0}
                                maxValue={100}
                                color={'foreground'}
                                aria-label={'volume slider'}
                                value={props.volume * 100}
                                onChange={props.onVolumeChangeHandler}
                                onChangeEnd={props.onVolumeSeekUp}
                                orientation={'vertical'}
                            />
                        </div>
                    }
                >
                    <button onClick={props.onMute} className="cursor-pointer">
                        {props.muted ? <PiSpeakerSimpleX className={styles.icon} /> :
                            <PiSpeakerHigh className={styles.icon} />}
                    </button>
                </Tooltip>
                <ProgressSlider played={props.played} playedSeconds={props.playedSeconds} onSeek={props.onSeek}
                                onSeekMouseUp={props.onSeekMouseUp}
                                onMouseSeekDown={props.onMouseSeekDown} chapters={props.chapters}
                                duration={props.duration} />
                <span className='text-default-50 whitespace-nowrap'>
                    {formatTime(props.currentTime)} : {formatTime(props.duration)}
                </span>
                <button onClick={props.toggleSubtitles} className="cursor-pointer">
                    {props.showSubtitles ? <PiRectangle className={styles.icon} /> :
                        <PiSubtitles className={styles.icon} />}
                </button>
                <Tooltip
                    closeDelay={1000}
                    classNames={{ content: 'p-1' }}
                    content={
                        <div
                            className='w-4'
                            onClick={(e: MouseEvent<HTMLDivElement>) => {
                                if (!speedSliderRef.current) return;
                                let rect = speedSliderRef.current.getBoundingClientRect();
                                const clickPercentage = (-(e.clientY - rect.bottom) / rect.height);
                                const speedPercentage = (clickPercentage * (video_speeds.maxSpeed - video_speeds.minSpeed) + video_speeds.minSpeed);
                                const roundedPercentage = Math.round(speedPercentage / video_speeds.step) * video_speeds.step;
                                props.setPlayBackRate(roundedPercentage);
                            }}
                        >
                            <Slider
                                ref={speedSliderRef}
                                size='sm'
                                className='h-32 w-4'
                                minValue={video_speeds.minSpeed}
                                maxValue={video_speeds.maxSpeed}
                                step={video_speeds.step}
                                color={'foreground'}
                                aria-label={'speed slider'}
                                value={props.playBackRate}
                                onChange={(value) => props.setPlayBackRate(value as number)}
                                orientation={'vertical'}
                                showSteps
                            />
                        </div>
                    }
                >
                    <button onClick={togglePlaybackRate} className='text-white cursor-pointer'>
                        {props.playBackRate}x
                    </button>
                </Tooltip>
                <button onClick={props.onToggleFullscreen} className="cursor-pointer">
                    <FaExpand className={styles.icon} />
                </button>
            </div>
        </div>
    );
};

export default Controls;
